import { SectionTitle, Sub, P, ApiBlock, FlowSteps } from "./GuideHelpers";

export const SectionDataSourcesAsin = () => (
  <>
    <SectionTitle id="data-sources">Data Sources & Flow</SectionTitle>
    <P>
      This section explains where Signal gets its data, how it moves between pages, and the external
      APIs and services involved. Understanding these flows is essential for backend integration.
    </P>

    <Sub id="ds-asin-onboarding">1. ASIN Onboarding — Search Query Performance Report (SQP)</Sub>
    <P>
      When a new ASIN is added to Signal, the <strong>first automated step</strong> is to populate the
      Keyword Bank with keywords from Amazon's Selling Partner Reports API.
    </P>
    <ApiBlock>
      <div className="font-semibold text-foreground mb-2">Amazon SP-API — Search Query Performance Report</div>
      <div>Report Type: <span className="text-foreground">GET_BRAND_ANALYTICS_SEARCH_QUERY_PERFORMANCE_REPORT</span></div>
      <div>Report Period: <span className="text-foreground">QUARTER</span> (most recent completed quarter)</div>
      <div>Key Fields: <span className="text-foreground">searchQuery</span>, <span className="text-foreground">asinPurchaseCount</span></div>
    </ApiBlock>
    <P>
      Signal requests the SQP report for the most recent completed quarter using the correct quarter
      start and end dates. The report returns query-level performance data for the ASIN, including
      each <code className="text-xs bg-surface-tint-medium px-1 py-0.5 rounded">searchQuery</code> and downstream
      metrics such as <code className="text-xs bg-surface-tint-medium px-1 py-0.5 rounded">asinPurchaseCount</code>.
    </P>
    <P>
      To build the Keyword Bank, Signal extracts all returned <code className="text-xs bg-surface-tint-medium px-1 py-0.5 rounded">searchQuery</code> values
      and sorts them by <code className="text-xs bg-surface-tint-medium px-1 py-0.5 rounded">asinPurchaseCount</code> descending.
      This produces a <strong>prioritized keyword list</strong> based on the search terms that most
      directly drove purchases for that ASIN during the latest quarter.
    </P>
    <FlowSteps
      items={[
        { actor: "user", label: "New ASIN added to Signal" },
        { actor: "signal", label: "Request SQP report from SP-API (latest quarter)" },
        { actor: "signal", label: "Extract searchQuery values, sort by asinPurchaseCount" },
        { actor: "signal", label: "Keywords populate the Keyword Bank (keyword text only)" },
      ]}
    />
    <P>
      <strong>Important:</strong> Keywords sourced exclusively from the SQP report enter the Keyword Bank
      as <strong>keyword text only</strong>. They will not have search volume, competitor counts, KPS,
      or any enriched metrics. These fields are populated later as landscape data becomes available.
    </P>

    <Sub id="ds-keyword-enrichment">2. Keyword Enrichment — Landscape Cross-Reference</Sub>
    <P>
      Over time, keywords in the Keyword Bank may overlap with keywords discovered through Landscapes.
      When a landscape generates data for a keyword (search volume, competitors, KPS, etc.), that data
      is <strong>automatically backfilled</strong> into the Keyword Bank entry for that keyword.
    </P>
    <P>
      Signal runs a <strong>daily cross-reference job</strong> that matches keywords in the Keyword Bank
      against all available landscape data. When a match is found, the keyword's enriched fields are
      updated with the latest available data. The goal is for every keyword in the Keyword Bank to
      carry the most current metrics.
    </P>
    <FlowSteps
      items={[
        { actor: "signal", label: "Daily job scans all Keyword Bank entries" },
        { actor: "signal", label: "Cross-references against all landscape keyword data" },
        { actor: "signal", label: "Matches update: search volume, competitors, KPS, trend, PPC" },
      ]}
    />

    <Sub id="ds-landscape-data">3. Landscape Data — Jungle Scout via Snowflake</Sub>
    <P>
      <strong>Competitor Identification.</strong> For any given priority search phrase, Signal identifies the
      <strong> top 10 organic search results</strong> on Amazon. These become the competitor ASINs for that
      landscape. The hero ASIN is compared against those 10 competitors, so every landscape query operates
      on <strong>11 ASINs total</strong> (1 hero + 10 competitors).
    </P>
    <P>
      Jungle Scout serves as the data source for two key pieces of Signal's landscaping feature:
    </P>
    <ul className="list-disc pl-5 space-y-2 text-sm text-muted-foreground mb-4">
      <li>
        <strong className="text-foreground">Product Enrichment</strong> — For the 11 ASINs
        (hero + 10 competitors), Signal pulls product metadata including brand, title, reviews, ratings,
        price, category ranks, and monthly sales estimates.
      </li>
      <li>
        <strong className="text-foreground">Reverse Keyword Lookup</strong> — For those same 11 ASINs,
        Signal retrieves the full keyword universe each ASIN has ranked for over a <strong>52-week
        lookback window</strong>, along with associated metrics:
        <ul className="list-disc pl-5 mt-1 space-y-1">
          <li>Estimated 30-day search volume</li>
          <li>Period-over-period volume change</li>
          <li>Year-over-year volume change</li>
          <li>Competitors (estimated number of other products associated with each keyword)</li>
          <li>Latest organic rank</li>
          <li>Latest rank date</li>
        </ul>
      </li>
    </ul>
    <ApiBlock>
      <div className="font-semibold text-foreground mb-2">Jungle Scout — Snowflake Data Share</div>
      <div>Access: <span className="text-foreground">Snowflake data share (direct query)</span></div>
      <div>Warehouse: <span className="text-foreground">Large</span></div>
      <div>Approx. Query Time: <span className="text-foreground">~1 min 20 sec per query</span></div>
      <div>Lookback: <span className="text-foreground">52 weeks</span></div>
      <div>ASINs per Query: <span className="text-foreground">11 (1 hero + 10 competitors)</span></div>
    </ApiBlock>
    <P>
      Both queries run against Jungle Scout's dataset in Snowflake via a data share. On a Large
      warehouse, each query completes in approximately 1 minute 20 seconds.
    </P>

    <Sub id="ds-landscape-phases">3a. Landscape Generation — Phased Approach</Sub>
    <P>
      Landscape generation is split into two phases so users get results immediately while
      suggested bid data populates in the background.
    </P>

    <div className="rounded-lg border border-border/60 bg-surface-tint-soft p-4 mb-4 space-y-4">
      <div>
        <div className="font-semibold text-foreground text-sm mb-1">Phase 1 — Generate the Landscape (Immediate)</div>
        <P>
          When a user requests a landscape, the system runs the reverse keyword lookup against
          Snowflake/JS and returns all keywords with their standard metrics (search volume, rank, etc.).
          The <strong>Suggested Bid</strong> column displays a <strong>"pending" indicator</strong> for
          all keywords. This response is instant from the user's perspective.
        </P>
      </div>

      <div className="border-t border-border/40 pt-4">
        <div className="font-semibold text-foreground text-sm mb-1">Phase 2 — Populate Suggested Bids (Background)</div>
        <P>
          Once the landscape is generated, a background job kicks off automatically:
        </P>
        <ol className="list-decimal pl-5 space-y-1 text-sm text-muted-foreground mb-2">
          <li>Keywords are sorted <strong className="text-foreground">descending by search volume</strong></li>
          <li>The job batches them in <strong className="text-foreground">groups of 100</strong> (Amazon's per-request limit), starting from the top</li>
          <li>As each batch resolves, the suggested bids are <strong className="text-foreground">written back in real time</strong> — so users see the most important keywords populate first, with the tail filling in shortly after</li>
          <li>The "pending" indicator clears on a <strong className="text-foreground">batch-by-batch basis</strong> as data arrives</li>
        </ol>
      </div>

      <div className="border-t border-border/40 pt-4">
        <div className="font-semibold text-foreground text-sm mb-1">Caching Layer</div>
        <P>
          Before hitting the Amazon Ads API for any keyword, the system first checks the cache:
        </P>
        <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground mb-2">
          <li>If a suggested bid exists for that keyword and was fetched within the last <strong className="text-foreground">24–48 hours</strong>, serve the cached value immediately — no API call needed</li>
          <li>If not, add it to the batch queue</li>
        </ul>
        <P>
          Over time, as users run landscapes across overlapping keyword sets, cache hit rates climb
          significantly and the volume of live API calls shrinks. Popular keywords in high-traffic
          categories may rarely need a fresh API call at all.
        </P>
      </div>
    </div>

    <FlowSteps
      items={[
        { actor: "user", label: "User requests landscape → reverse keyword lookup runs instantly" },
        { actor: "signal", label: "Landscape returned with all metrics; Suggested Bid shows 'pending'" },
        { actor: "signal", label: "Background job batches keywords (100/batch) sorted by search volume" },
        { actor: "signal", label: "Each batch checks cache first (24–48 hr TTL), then hits Amazon Ads API" },
        { actor: "signal", label: "Bids written back in real time; pending indicators clear per batch" },
      ]}
    />

    <P>
      <strong>Net result:</strong> Users get their landscape instantly, see high-priority keywords populate
      with bid data within seconds, and the long tail fills in shortly after — all without ever hitting
      a loading wall.
    </P>
  </>
);
